/** Product surface flags — flip when features are ready for general availability. */
export const STRIPE_CONNECT_ENABLED = false;

/** Wrangler / Worker env key — set to `true` when live OAuth + sync are ready in production. */
export const INTEGRATIONS_LIVE_ENV_KEY = 'INTEGRATIONS_LIVE_ENABLED';

/**
 * Legacy Renewal Drive product surface (customers, campaigns, risk, billing, etc.).
 * Worker: set LEGACY_RENEWAL_ENABLED=true. SPA: set VITE_LEGACY_RENEWAL=true at build time.
 * Default OFF — Growth Kit is the primary product.
 */
export const LEGACY_RENEWAL_ENV_KEY = 'LEGACY_RENEWAL_ENABLED';

type EnvLike = Record<string, unknown> | ((key: string) => string | undefined);

function envGetter(env: EnvLike): (key: string) => string | undefined {
  return typeof env === 'function'
    ? env
    : (key: string) => {
        const v = env[key];
        return typeof v === 'string' && v.length ? v : undefined;
      };
}

function isTruthyFlag(raw: string | undefined): boolean {
  const v = raw?.trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'yes';
}

/** Live QuickBooks / Jobber / Housecall Pro connect + sync (off until OAuth apps are configured). */
export function integrationsLiveEnabled(env: EnvLike): boolean {
  return isTruthyFlag(envGetter(env)(INTEGRATIONS_LIVE_ENV_KEY));
}

/** Whether the legacy Renewal Drive UI/API/crons are active (default: false). */
export function legacyRenewalEnabled(env: EnvLike = {}): boolean {
  return isTruthyFlag(envGetter(env)(LEGACY_RENEWAL_ENV_KEY));
}

/** Improvement Kit AI wording adapter (Spec 7.8 / K-10). Default OFF. */
export const AI_IMPROVEMENT_ENV_KEY = 'AI_IMPROVEMENT_ENABLED';

export function aiImprovementEnabled(env: EnvLike = {}): boolean {
  return isTruthyFlag(envGetter(env)(AI_IMPROVEMENT_ENV_KEY));
}

/**
 * Outbound email via Resend/Postmark (verify, reset, support, kit alerts).
 * Default OFF — set EMAIL_SENDING_ENABLED=true once mail DNS + Resend are ready.
 * When off, auth codes/links are printed to Worker logs (`wrangler tail`) for testing.
 */
export const EMAIL_SENDING_ENV_KEY = 'EMAIL_SENDING_ENABLED';

export function emailSendingEnabled(env: EnvLike = {}): boolean {
  return isTruthyFlag(envGetter(env)(EMAIL_SENDING_ENV_KEY));
}

/** SPA helper — reads Vite `VITE_AI_IMPROVEMENT` (default off). */
export function aiImprovementEnabledClient(): boolean {
  const metaEnv = (import.meta as { env?: { VITE_AI_IMPROVEMENT?: string } }).env;
  return isTruthyFlag(metaEnv?.VITE_AI_IMPROVEMENT);
}

/** SPA helper — reads Vite build-time `VITE_LEGACY_RENEWAL` (default off). */
export function legacyRenewalEnabledClient(): boolean {
  const metaEnv = (import.meta as { env?: { VITE_LEGACY_RENEWAL?: string } }).env;
  return isTruthyFlag(metaEnv?.VITE_LEGACY_RENEWAL);
}

export const INTEGRATIONS_COMING_SOON_FEATURE =
  'App integrations (QuickBooks, Jobber, Housecall Pro) — coming soon';

export const INTEGRATIONS_LIVE_PRO_FEATURE = 'Integrations: QuickBooks, Jobber, Housecall Pro';

/** Integrations shown in app UI and connect flows (code for others remains in repo). */
export const VISIBLE_INTEGRATION_PROVIDER_KEYS = ['quickbooks', 'housecallpro', 'jobber'] as const;

export type VisibleIntegrationProviderKey = (typeof VISIBLE_INTEGRATION_PROVIDER_KEYS)[number];

export function isVisibleIntegrationProvider(key: string): key is VisibleIntegrationProviderKey {
  return (VISIBLE_INTEGRATION_PROVIDER_KEYS as readonly string[]).includes(key);
}

export type StaticPaymentLinkProvider = 'paypal' | 'stripe' | 'square' | 'venmo' | 'zelle' | 'other';

export interface StaticPaymentLinkPreset {
  provider: StaticPaymentLinkProvider;
  label: string;
  placeholder: string;
  hint: string;
}

/** Presets shops can add on Settings → Renewal page links. */
export const STATIC_PAYMENT_LINK_PRESETS: StaticPaymentLinkPreset[] = [
  {
    provider: 'other',
    label: 'Renewal page link',
    placeholder: 'https://yourshop.com/renew',
    hint: 'A page on your website, invoice portal, or booking site where customers complete renewal. This product does not process payments.',
  },
];

export interface StaticPaymentLink {
  id: string;
  provider: StaticPaymentLinkProvider | string;
  label: string;
  url: string;
}

export function parseStaticPaymentLinks(raw: string | null | undefined): StaticPaymentLink[] {
  if (!raw?.trim()) return [];
  try {
    const parsed = JSON.parse(raw) as StaticPaymentLink[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((l) => l && typeof l.url === 'string' && l.url.trim().length > 0);
  } catch {
    return [];
  }
}

export function resolveDefaultStaticPaymentLink(
  links: StaticPaymentLink[],
  defaultId: string | null | undefined,
): string | undefined {
  if (!links.length) return undefined;
  const byId = defaultId ? links.find((l) => l.id === defaultId && l.url.trim()) : undefined;
  return (byId ?? links.find((l) => l.url.trim()))?.url.trim();
}

/** Whether renewal emails can include a pay link (static URLs or Stripe Connect when enabled). */
export function shopHasPaymentLinks(settings: {
  staticPaymentLinks?: string | null;
  defaultStaticPaymentLinkId?: string | null;
  stripeAccountId?: string | null;
}): boolean {
  if (parseStaticPaymentLinks(settings.staticPaymentLinks).some((l) => l.url.trim())) return true;
  return STRIPE_CONNECT_ENABLED && !!settings.stripeAccountId;
}
